import React, { FC, useState, useCallback, useRef, useEffect } from "react";
import {
  Edit3,
  Upload,
  Trash2,
  FileText,
  FileInput,
  FileOutput,
  Copy,
  Check,
  Download,
  Wand2,
  ArrowLeftRight,
  ChevronDown,
} from "lucide-react";
import { useAlert } from "../../hooks/useAlert";
import { AlertMessage } from "../../components/common/AlertMessage";
import { getDictDataListSimpleApi } from "../../service/dict";
import {
  postSubtitleTranslateTextApi,
  postSubtitleTranslateSrtApi,
  getSubtitleTranslateDownloadApi,
} from "../../service/subtitleTrans";
import "./SubtitleTrans.scss";

type Mode = "text" | "file";

const SRT_PLACEHOLDER = [
  "1",
  "00:00:00,300 --> 00:00:03,200",
  "让我们掌声欢迎两位新人入场",
  "",
  "2",
  "00:00:08,413 --> 00:00:10,757",
  "哥，你穿浴袍来参加婚礼",
  "",
].join("\n");

interface LangOption {
  code: string;
  label: string;
  flag: string;
}

export const SubtitleTrans: FC = () => {
  const { alertConfig, showError, hideAlert } = useAlert();
  const [mode, setMode] = useState<Mode>("text");
  const [inputText, setInputText] = useState(SRT_PLACEHOLDER);
  const [langList, setLangList] = useState<LangOption[]>([]);
  const [srcLang, setSrcLang] = useState<LangOption | null>(null);
  const [tgtLang, setTgtLang] = useState<LangOption | null>(null);
  const [srcOpen, setSrcOpen] = useState(false);
  const [tgtOpen, setTgtOpen] = useState(false);
  const [outputText, setOutputText] = useState("");
  const [outputLabel, setOutputLabel] = useState("等待翻译");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 轮询取消标志：组件卸载时置 true，终止 for 循环内的后续 setState
  const pollCancelledRef = useRef<boolean>(false);

  // 从接口加载语种列表
  useEffect(() => {
    const fetchLangs = async () => {
      try {
        const res = await getDictDataListSimpleApi("target_language");
        if (res.code === 0 && Array.isArray(res.data)) {
          const list = res.data.map((d: any) => ({
            code: d.value,
            label: d.name,
            flag: "",
          }));
          setLangList(list);
          if (list.length > 0) {
            setSrcLang(list[0]);
            if (list.length > 1) setTgtLang(list[1]);
          }
        }
      } catch (e) {
        console.error("获取语种列表失败:", e);
      }
    };
    fetchLangs();
  }, []);

  const charCount = inputText.length;

  const swapLangs = useCallback(() => {
    setDownloadUrl(null);
    setOutputText("");
    setOutputLabel("等待翻译");
    setSrcLang(tgtLang);
    setTgtLang(srcLang);
  }, [srcLang, tgtLang]);

  const clearInput = useCallback(() => {
    setInputText("");
    setOutputText("");
    setDownloadUrl(null);
    setOutputLabel("等待翻译");
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // 轮询翻译任务状态
  const pollTaskStatus = useCallback(async (taskId: string) => {
    const MAX_POLL = 120; // 最多轮询 120 次（约 2 分钟）
    for (let i = 0; i < MAX_POLL; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      // 组件卸载后立即终止轮询，避免 setState on unmounted component
      if (pollCancelledRef.current) return;
      try {
        const res = await getSubtitleTranslateDownloadApi(taskId);
        if (pollCancelledRef.current) return;
        const { status, srt_text, error, progress, message } = res.data;
        setOutputLabel(`翻译中 ${progress ?? 0}%`);
        if (status === "completed") {
          if (srt_text) setOutputText(srt_text);
          if (res.data.downloadUrl) setDownloadUrl(res.data.downloadUrl);
          setOutputLabel(message || "翻译完成");
          return;
        }
        if (status === "failed") {
          showError(error || "翻译失败");
          setOutputLabel("翻译失败");
          return;
        }
      } catch {
        // 网络异常继续轮询
      }
    }
    if (pollCancelledRef.current) return;
    showError("翻译超时，请稍后重试");
    setOutputLabel("翻译超时");
  }, []);

  // 开始翻译
  const startTranslate = useCallback(async () => {
    if (!inputText.trim() || !srcLang || !tgtLang) return;
    setIsTranslating(true);
    setOutputText("");
    setDownloadUrl(null);
    setOutputLabel("翻译中…");
    try {
      if (mode === "text") {
        // 文本模式：将输入文本按行保留，用 \n 拼接传给接口
        const text = inputText.trim();
        const res = await postSubtitleTranslateTextApi({
          sourceLanguage: srcLang.code,
          targetLanguage: tgtLang.code,
          text,
        });
        if (res.code !== 0) {
          showError(res.msg || "翻译请求失败");
          setOutputLabel("翻译失败");
          return;
        }
        // 如果接口直接返回了 srt_text（已完成）
        if (res.data.srt_text) {
          setOutputText(res.data.srt_text);
          setOutputLabel("翻译完成");
        } else {
          // 轮询等待
          await pollTaskStatus(res.data.taskId);
        }
      } else {
        // 文件模式：上传 SRT 文件
        if (!uploadedFile) return;
        const res = await postSubtitleTranslateSrtApi({
          sourceLanguage: srcLang.code,
          targetLanguage: tgtLang.code,
          file: uploadedFile,
        });
        if (res.code !== 0) {
          showError(res.msg || "上传翻译失败");
          setOutputLabel("翻译失败");
          return;
        }
        await pollTaskStatus(res.data.taskId);
      }
    } catch (e: any) {
      showError(e?.message || "翻译请求失败");
      setOutputLabel("翻译失败");
    } finally {
      setIsTranslating(false);
    }
  }, [
    mode,
    inputText,
    uploadedFile,
    srcLang,
    tgtLang,
    pollTaskStatus,
    showError,
  ]);

  // 文件处理
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith(".srt")) {
      showError("仅支持 .srt 格式文件");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError("文件大小超过 5MB 限制");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setInputText(text);
      setUploadedFile(file);
    };
    reader.readAsText(file);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.[0]) processFile(e.target.files[0]);
    },
    [processFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) processFile(e.dataTransfer.files[0]);
    },
    [processFile],
  );

  const removeFile = useCallback(() => {
    setDownloadUrl(null);
    setOutputText("");
    setOutputLabel("等待翻译");
    setUploadedFile(null);
    setInputText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // 复制译文
  const [showCopyTip, setShowCopyTip] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyResult = useCallback(() => {
    if (outputText) {
      navigator.clipboard.writeText(outputText);
      setShowCopyTip(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setShowCopyTip(false), 2000);
    }
  }, [outputText]);

  const handleDownloadSrt = useCallback(() => {
    if (!outputText) return;
    const blob = new Blob([`﻿${outputText}`], {
      type: "text/srt;charset=utf-8",
    });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = "translated_subtitle.srt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, [outputText]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      // 标记轮询取消，终止进行中的 for 循环
      pollCancelledRef.current = true;
    };
  }, []);

  return (
    <div className="st-page">
      <AlertMessage config={alertConfig} onClose={hideAlert} />
      <p className="st-subtitle">
        输入文本或上传 SRT 字幕文件，选择语言方向，一键获取高质量译文
      </p>

      {/* 顶部语言选择 + 模式切换 */}
      <div className="st-lang-bar-card">
        <div className="st-lang-bar">
          {/* 源语言 */}
          <div className="st-lang-btn-wrap">
            <button
              className="st-lang-btn"
              onClick={() => {
                setSrcOpen(!srcOpen);
                setTgtOpen(false);
              }}
            >
              <span className="st-lang-flag">{srcLang?.flag}</span>
              <span className="st-lang-name">{srcLang?.label || "请选择"}</span>
              <ChevronDown size={14} />
            </button>
            {srcOpen && (
              <div className="st-lang-popover">
                {langList.map((l) => (
                  <div
                    key={l.code}
                    className="st-lang-option"
                    onClick={() => {
                      setDownloadUrl(null);
                      setOutputText("");
                      setOutputLabel("等待翻译");
                      setSrcLang(l);
                      setSrcOpen(false);
                    }}
                  >
                    <span>{l.flag}</span> {l.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 交换按钮 */}
          <button className="st-lang-swap" onClick={swapLangs} title="交换语言">
            <ArrowLeftRight size={16} />
          </button>

          {/* 目标语言 */}
          <div className="st-lang-btn-wrap">
            <button
              className="st-lang-btn"
              onClick={() => {
                setTgtOpen(!tgtOpen);
                setSrcOpen(false);
              }}
            >
              <span className="st-lang-flag">{tgtLang?.flag}</span>
              <span className="st-lang-name">{tgtLang?.label || "请选择"}</span>
              <ChevronDown size={14} />
            </button>
            {tgtOpen && (
              <div className="st-lang-popover">
                {langList.map((l) => (
                  <div
                    key={l.code}
                    className="st-lang-option"
                    onClick={() => {
                      setDownloadUrl(null);
                      setOutputText("");
                      setOutputLabel("等待翻译");
                      setTgtLang(l);
                      setTgtOpen(false);
                    }}
                  >
                    <span>{l.flag}</span> {l.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="st-lang-divider" />

          {/* 模式切换 */}
          <button
            className={`st-mode-tab ${mode === "text" ? "active" : ""} ${isTranslating && mode !== "text" ? "tab-muted" : ""}`}
            onClick={() => {
              if (isTranslating) return;
              setMode("text");
              setInputText(SRT_PLACEHOLDER);
              setOutputText("");
              setDownloadUrl(null);
              setOutputLabel("等待翻译");
            }}
          >
            <Edit3 size={14} /> 输入文本
          </button>
          <button
            className={`st-mode-tab ${mode === "file" ? "active" : ""} ${isTranslating && mode !== "file" ? "tab-muted" : ""}`}
            onClick={() => {
              if (isTranslating) return;
              setMode("file");
              setUploadedFile(null);
              setInputText("");
              setOutputText("");
              setDownloadUrl(null);
              setOutputLabel("等待翻译");
            }}
          >
            <Upload size={14} /> 上传 SRT
          </button>

          <div style={{ flex: 1 }} />

          {/* 清空 */}
          <button className="st-clear-btn" onClick={clearInput}>
            <Trash2 size={12} /> 清空
          </button>
        </div>
      </div>

      {/* 左右对照区 */}
      <div className="st-panels">
        {/* 左侧：原文 */}
        <div className="st-panel">
          <div className="st-panel-header">
            <span className="st-panel-title">
              <FileInput size={14} /> 原文
            </span>
            <span className="st-panel-meta">{charCount} 字符</span>
          </div>
          {mode === "text" ? (
            <textarea
              className="st-textarea"
              placeholder={SRT_PLACEHOLDER}
              value={inputText}
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
              data-gramm="false"
              onChange={(e) => setInputText(e.target.value)}
            />
          ) : (
            <div className="st-file-area">
              {!uploadedFile ? (
                <div
                  className={`st-upload-zone ${isDragging ? "dragover" : ""}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <div className="st-upload-icon">
                    <FileText size={28} />
                  </div>
                  <div className="st-upload-title">
                    点击上传或拖拽 SRT 字幕文件到此处
                  </div>
                  <div className="st-upload-hint">
                    仅支持 .srt 格式 · 文件最大 1MB
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".srt"
                    style={{ display: "none" }}
                    onChange={handleFileSelect}
                  />
                </div>
              ) : (
                <div className="st-file-card">
                  <div className="st-file-card-icon">
                    <FileText size={20} color="#fff" />
                  </div>
                  <div className="st-file-card-info">
                    <div className="st-file-card-name">{uploadedFile.name}</div>
                    <div className="st-file-card-meta">
                      {(uploadedFile.size / 1024).toFixed(1)} KB · SRT
                    </div>
                  </div>
                  <button
                    className="st-file-card-delete"
                    onClick={removeFile}
                    title="删除"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 右侧：译文 */}
        <div className="st-panel">
          <div className="st-panel-header">
            <span className="st-panel-title">
              <FileOutput size={14} /> 译文
            </span>
            <div className="st-panel-header-right">
              <span className="st-panel-meta">{outputLabel}</span>
              {outputText && (
                <button
                  className={`st-copy-btn${showCopyTip ? " st-copy-btn--copied" : ""}`}
                  onClick={copyResult}
                >
                  {showCopyTip ? (
                    <>
                      <Check size={12} /> 已复制
                    </>
                  ) : (
                    <Copy size={12} />
                  )}
                </button>
              )}
              {outputText && (
                <button className="st-export-btn" onClick={handleDownloadSrt}>
                  <Download size={12} /> 导出 SRT
                </button>
              )}
            </div>
          </div>
          <textarea
            className="st-textarea readonly"
            readOnly
            placeholder="翻译结果将在此处显示…"
            value={outputText}
          />
        </div>
      </div>

      {/* 底部按钮 */}
      <button
        className="st-btn-generate"
        disabled={isTranslating || !inputText.trim()}
        onClick={startTranslate}
      >
        {isTranslating ? (
          <>
            <span className="st-spin">
              <Wand2 size={20} />
            </span>{" "}
            翻译中…
          </>
        ) : (
          <>
            <Wand2 size={20} /> 开始翻译
          </>
        )}
      </button>
    </div>
  );
};
